// src/data/presets.js
// Demo arrangements that showcase ensemble blending.
//
// Each preset is { name, description, bpm, tracks: [{ instrumentId, name, mixer, notes: [...] }] }
// `notes` use MIDI pitch numbers and time in seconds (matching Track.js).

// MIDI note helpers
const N = (pitch, time, duration, velocity = 0.8) => ({ pitch, time, duration, velocity });


/**
 * String quartet: 2 violins, viola (cello proxy), cello. Simple chord progression
 * with melody on violin 1, sustained pads on others.
 */
const stringQuartet = {
      name: 'String quartet', nameZh: '弦乐四重奏',
  description: 'Two violins, viola, and cello in a slow harmonic progression.',
  descriptionZh: '两把小提琴、一把中提琴与一把大提琴，缓慢的和声进行。',
  bpm: 80,
  tracks: [
    {
      instrumentId: 'violin',
      name: 'Violin I — melody',
      mixer: { volume: 0, pan: -0.3, reverbSend: 0.5 },
      notes: melodyViolin()
    },
    {
      instrumentId: 'violin',
      name: 'Violin II — harmony',
      mixer: { volume: -3, pan: -0.1, reverbSend: 0.55 },
      notes: harmonyLine([60, 62, 64, 65, 67], 16, 1.5, 0.6)
    },
    {
      instrumentId: 'cello',
      name: 'Cello — bass',
      mixer: { volume: -2, pan: 0.2, reverbSend: 0.5 },
      notes: bassLine([48, 53, 55, 57, 53], 16, 2.0, 0.7)
    },
    {
      instrumentId: 'harp',
      name: 'Harp — arpeggios',
      mixer: { volume: -4, pan: 0.3, reverbSend: 0.65 },
      notes: pluckArpeggio([60, 64, 67, 72], 16, 0.5, 0.5)
    }
  ]
};

/**
 * Clarinet · Trumpet · Guitar trio: a calm, lyrical D-minor piece.
 * Clarinet carries the melody, trumpet holds soft sustained harmonies
 * on the chord roots, guitar plays arpeggios.
 */
const clarinetTrio = {
  name: 'Clarinet · Trumpet · Guitar trio', nameZh: '单簧管 · 小号 · 吉他三重奏',
  description: 'Lyrical clarinet melody over trumpet harmonies and guitar arpeggios in D minor.',
  descriptionZh: 'D小调抒情单簧管旋律，小号和声铺垫，吉他琶音伴奏。',
  bpm: 100,
  tracks: [
    {
      instrumentId: 'clarinet',
      name: 'Clarinet — lead melody',
      mixer: { volume: 0, pan: -0.2, reverbSend: 0.45 },
      notes: [
        // Phrase 1 (bars 1-4, 0.0s-9.6s) — opens on D, walks up to F
        N(62, 0.0, 1.1, 0.9),  N(65, 1.2, 1.1, 0.9),  // Bar 1 Dm: D4 q, F4 q
        N(62, 2.4, 0.5, 0.85), N(65, 3.0, 0.5, 0.85), N(69, 3.6, 1.2, 0.9),   // Bar 2 Bb: D4 8th, F4 8th, A4 q
        N(65, 4.8, 0.6, 0.85), N(69, 5.4, 0.6, 0.85), N(72, 6.0, 1.2, 0.9),   // Bar 3 F: F4 8th, A4 8th, C5 q
        N(69, 7.2, 0.6, 0.85), N(65, 7.8, 0.6, 0.85), N(62, 8.4, 1.2, 0.9),   // Bar 4 C: A4 8th, F4 8th, D4 q
        // Phrase 2 (bars 5-8, 9.6s-19.2s) — rises to D5/F5, returns
        N(62, 9.6, 0.9, 0.9),  N(65, 10.5, 0.6, 0.85), N(69, 11.1, 0.9, 0.9), // Bar 5 Dm: D4, F4, A4
        N(74, 12.0, 1.1, 0.9), N(77, 13.2, 1.0, 0.9),                         // Bar 6 Bb: D5, F5 (high)
        N(74, 14.4, 0.5, 0.85), N(77, 15.0, 0.5, 0.85), N(72, 15.6, 0.9, 0.85), // Bar 7 F: D5 8th, F5 8th, C5 q
        N(65, 16.8, 0.6, 0.85), N(62, 17.4, 0.6, 0.85), N(62, 18.0, 1.2, 0.9)  // Bar 8 C: F4 8th, D4 8th, D4 q (resolution)
      ]
    },
    {
      instrumentId: 'trumpet',
      name: 'Trumpet — sustained harmonies',
      mixer: { volume: -4, pan: 0.25, reverbSend: 0.55 },
      // Sustained chord roots, one per bar, picked from the trumpet's
      // strong sample range (A3, F3, C4, G4 are direct samples).
      // At 100 BPM, 1 bar = 2.4s. Notes are held for slightly less than
      // a bar to leave a brief gap before the next chord.
      notes: [
        // Bars 1-4 (first chord cycle)
        N(57, 0.0,  2.2, 0.7),  // Bar 1 Dm: A3
        N(53, 2.4,  2.2, 0.7),  // Bar 2 Bb: F3
        N(60, 4.8,  2.2, 0.7),  // Bar 3 F:  C4
        N(67, 7.2,  2.2, 0.7),  // Bar 4 C:  G4
        // Bars 5-8 (second chord cycle, slightly softer)
        N(57, 9.6,  2.2, 0.6),  // Bar 5 Dm: A3
        N(53, 12.0, 2.2, 0.6),  // Bar 6 Bb: F3
        N(60, 14.4, 2.2, 0.6),  // Bar 7 F:  C4
        N(67, 16.8, 2.2, 0.65)  // Bar 8 C:  G4 (resolution)
      ]
    },
    {
      instrumentId: 'guitar',
      name: 'Guitar — arpeggios',
      mixer: { volume: -5, pan: 0.0, reverbSend: 0.6 },
      // D-minor arpeggio cycled at 8th-note rate across the 19.2s arrangement.
      notes: pluckArpeggio([62, 65, 69, 72, 69, 65], 19.2, 0.3, 0.55)
    }
  ]
};

/**
 * Flute + Harp + Piano trio: a calm, ambient piece.
 */
const fluteHarpPiano = {
     name: 'Flute · Harp · Piano trio', nameZh: '长笛 · 竖琴 · 钢琴三重奏',
  description: 'Flute carries the melody over a piano pad with harp arpeggios.',
  descriptionZh: '长笛演奏旋律，钢琴铺垫和声，竖琴琶音伴奏。',
  bpm: 84,
  tracks: [
    {
      instrumentId: 'flute',
      name: 'Flute — melody',
      mixer: { volume: 0, pan: 0, reverbSend: 0.5 },
      notes: [
        N(72, 0.0, 1.5, 0.9),
        N(74, 1.6, 0.8, 0.85),
        N(76, 2.5, 1.5, 0.9),
        N(74, 4.1, 0.8, 0.85),
        N(72, 5.0, 1.5, 0.9),
        N(69, 6.6, 0.8, 0.85),
        N(72, 7.5, 1.5, 0.9),
        N(76, 9.1, 1.0, 0.9),
        N(79, 10.2, 1.8, 0.95)
      ]
    },
    {
      instrumentId: 'piano',
      name: 'Piano — pad chords',
      mixer: { volume: -4, pan: 0.0, reverbSend: 0.6 },
      notes: chordPad([
        [60, 64, 67],   // C
        [62, 65, 69],   // Dm
        [64, 67, 71],   // Em
        [65, 69, 72],   // F
        [60, 64, 67]    // C
      ], 12, 2.4, 0.45)
    },
    {
      instrumentId: 'harp',
      name: 'Harp — arpeggios',
      mixer: { volume: -5, pan: 0.25, reverbSend: 0.7 },
      notes: pluckArpeggio([60, 64, 67, 72, 67, 64], 12, 0.3, 0.5)
    }
  ]
};

export const PRESETS = [stringQuartet, fluteHarpPiano, clarinetTrio];

// ---------------- note generators ----------------

/** Pluck arpeggio: cycle through `pitches` for `totalSeconds`, note = `noteDur`. */
function pluckArpeggio(pitches, totalSeconds, noteDur, velocity) {
  const out = [];
  let t = 0;
  let i = 0;
  while (t < totalSeconds) {
    out.push(N(pitches[i % pitches.length], t, noteDur, velocity));
    t += noteDur;
    i++;
  }
  return out;
}

/** Bass line: cycle through root pitches, each note `noteDur` seconds. */
function bassLine(pitches, totalSeconds, noteDur, velocity) {
  return pluckArpeggio(pitches, totalSeconds, noteDur, velocity);
}

/** Harmony line: long held notes cycling through `pitches`. */
function harmonyLine(pitches, totalSeconds, noteDur, velocity) {
  const out = [];
  let t = 0;
  let i = 0;
  while (t < totalSeconds) {
    out.push(N(pitches[i % pitches.length], t, noteDur, velocity));
    t += noteDur;
    i++;
  }
  return out;
}

/** Violin I melody — a lyrical line over the bass. */
function melodyViolin() {
  // A simple melodic line in A minor
  const phrase = [
    [69, 0.0, 0.9], [72, 1.0, 0.45], [74, 1.5, 0.45], [76, 2.0, 0.9],
    [77, 3.0, 0.45], [76, 3.5, 0.45], [74, 4.0, 0.9], [72, 5.0, 0.9],
    [69, 6.0, 0.45], [72, 6.5, 0.45], [74, 7.0, 0.9], [76, 8.0, 0.45],
    [77, 8.5, 0.45], [79, 9.0, 0.9], [77, 10.0, 0.45], [76, 10.5, 0.45],
    [74, 11.0, 1.0], [72, 12.0, 0.45], [69, 12.5, 1.5]
  ];
  return phrase.map(([p, t, d]) => N(p, t, d, 0.8));
}

/** Chord pad: each chord is held for `chordDur` seconds, arpeggiated as block chord. */
function chordPad(chords, totalSeconds, chordDur, velocity) {
  const out = [];
  let t = 0;
  let i = 0;
  while (t < totalSeconds) {
    const chord = chords[i % chords.length];
    for (const p of chord) {
      out.push(N(p, t, chordDur * 0.95, velocity));
    }
    t += chordDur;
    i++;
  }
  return out;
}
